// Форматирование для интерфейса: даты ДД.ММ.ГГГГ, числа с запятой, длительности «1 ч 05 мин».
// Пустое значение везде показывается прочерком.

export const DASH = '—'
const MINUS = String.fromCharCode(0x2212)
export const NBSP = String.fromCharCode(0xa0)
const NARROW_NBSP = new RegExp(String.fromCharCode(0x202f), 'g')

type DateInput = Date | string | number | null | undefined

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/** Date из Date, ISO-даты ('2026-10-16' — местная полночь, без сдвига) или ISO-времени. */
export function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  if (typeof value === 'string' && ISO_DATE.test(value)) {
    const [y, m, d] = value.split('-').map(Number) as [number, number, number]
    return new Date(y, m - 1, d)
  }
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const pad = (n: number) => String(n).padStart(2, '0')

export function formatDate(value: DateInput): string {
  const d = toDate(value)
  if (!d) return DASH
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`
}

/** «16.10» — короткая дата для осей и списков внутри одного года. */
export function formatDayMonth(value: DateInput): string {
  const d = toDate(value)
  if (!d) return DASH
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}`
}

export function formatTime(value: DateInput): string {
  const d = toDate(value)
  if (!d) return DASH
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function formatDateTime(value: DateInput): string {
  const d = toDate(value)
  if (!d) return DASH
  return `${formatDate(d)} ${formatTime(d)}`
}

const WEEKDAYS = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота']
const MONTHS_GEN = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
]
export const MONTHS_SHORT = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек']

/** «пятница, 16 октября» — заголовок дня. */
export function formatDayTitle(value: DateInput): string {
  const d = toDate(value)
  if (!d) return DASH
  return `${WEEKDAYS[d.getDay()]}, ${d.getDate()} ${MONTHS_GEN[d.getMonth()]}`
}

/** «окт 2026» — подпись месяца на длинных осях. */
export function formatMonthYear(value: DateInput): string {
  const d = toDate(value)
  if (!d) return DASH
  return `${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`
}

export type NumberFormat = {
  /** Максимум знаков после запятой (по умолчанию 1). */
  digits?: number
  /** Всегда показывать ровно `digits` знаков: 80,0. */
  fixed?: boolean
}

const numberFormats = new Map<string, Intl.NumberFormat>()

function numberFormat(digits: number, fixed: boolean): Intl.NumberFormat {
  const key = `${digits}:${fixed}`
  let nf = numberFormats.get(key)
  if (!nf) {
    nf = new Intl.NumberFormat('ru-RU', {
      maximumFractionDigits: digits,
      minimumFractionDigits: fixed ? digits : 0,
    })
    numberFormats.set(key, nf)
  }
  return nf
}

export function formatNumber(value: number | null | undefined, opts: NumberFormat = {}): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH
  const text = numberFormat(opts.digits ?? 1, opts.fixed ?? false).format(value)
  // Intl для ru-RU разделяет тысячи узким неразрывным пробелом — приводим к обычному неразрывному.
  return text.replace(NARROW_NBSP, NBSP).replace('-', MINUS)
}

/** Изменение со знаком: «+0,4», «−1,2», «0». */
export function formatSigned(value: number | null | undefined, opts: NumberFormat = {}): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH
  const text = formatNumber(Math.abs(value), opts)
  if (text === '0' || /^0(,0+)?$/.test(text)) return text
  return (value > 0 ? '+' : MINUS) + text
}

/** Длительность словами: «1 ч 05 мин», «45 мин», «40 с». */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return DASH
  const total = Math.max(0, Math.round(seconds))
  if (total > 0 && total < 60) return `${total} с`
  const minutes = Math.round(total / 60)
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return h > 0 ? `${h} ч ${pad(m)} мин` : `${m} мин`
}

/** Время на часах таймера: «1:05», «1:02:03». */
export function formatClock(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
}

/** Русское множественное число: pluralRu(5, ['день', 'дня', 'дней']) → 'дней'. */
export function pluralRu(n: number, forms: readonly [string, string, string]): string {
  const abs = Math.abs(Math.trunc(n))
  const mod10 = abs % 10
  const mod100 = abs % 100
  if (mod10 === 1 && mod100 !== 11) return forms[0]
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1]
  return forms[2]
}

/** Разбор числа, введённого человеком: запятая или точка, пробелы. Пустое или мусор — null. */
export function parseDecimal(text: string): number | null {
  const cleaned = text.replace(/\s/g, '').replace(',', '.')
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return null
  const value = Number(cleaned)
  return Number.isFinite(value) ? value : null
}
